<template>
  <div v-if="variant === 'card'" class="zoox-notification-center-container">
    <v-overlay :value="opened" @click="onClickClose"></v-overlay>
    <notification-card
      v-bind="$props"
      card-height="100vh"
      @click:close="onClickClose"
      @click:clear="onClickClear"
      @click:remove="onClickRemove"
      @click:notification="onClickNotification"
      @click:readed="onClickReaded"
    >
      <template
        v-if="$scopedSlots['notification']"
        #notification="{notification}"
      >
        <slot name="notification" :notification="notification">
          {{ notification.text }}
        </slot>
      </template>
    </notification-card>
  </div>

  <v-menu
    v-else-if="variant === 'menu'"
    v-model="isMenuOpened"
    v-bind="menuProps"
  >
    <template #activator="{on, attrs, value}">
      <!--@slot Slot de ativação do menu quando a variante `menu` é usada -->
      <div style="position: relative">
        <slot name="activator" :on="on" :attrs="attrs" :value="value">
          <z-icon v-bind="attrs" v-on="on">$notifications_rounded</z-icon>
        </slot>

        <v-fade-transition>
          <div v-if="value && showMenuArrow" class="arrow-menu"></div>
        </v-fade-transition>
      </div>
    </template>

    <notification-card
      v-bind="$props"
      :card-height="maxMenuHeigth"
      @click:close="isMenuOpened = false"
      @click:clear="onClickClear"
      @click:remove="onClickRemove"
      @click:notification="onClickNotification"
      @click:readed="onClickReaded"
    >
      <template
        v-if="$scopedSlots['notification']"
        #notification="{notification}"
      >
        <!--@slot Slot que permite a customização da notificação
            @binding {Object} notification - Objeto com os dados da notificação
        -->
        <slot name="notification" :notification="notification">
          {{ notification.text }}
        </slot>
      </template>
    </notification-card>
  </v-menu>
</template>

<script>
import NotificationCard from './private/NotificationCard.vue'

/**
 * @displayName Notification Center
 */
export default {
  name: 'ZNotificationCenter',
  components: {
    NotificationCard,
  },
  props: {
    /**
     * Título do card das notificações
     */
    title: {
      type: String,
    },
    /**
     * Flag para indicar que está aberto ao usar a variante `card`
     */
    opened: {
      type: Boolean,
      default: false,
    },
    /**
     * Array de notificações a serem exibidas
     *
     * @typedef {text: string}
     */
    notifications: {
      type: Array,
      default: () => [],
    },
    /**
     * Define o ícone de remoção da notificação
     */
    closeIcon: {
      type: String,
      default: '$close_rounded',
    },

    /**
     * Define a variante do componente
     *
     * @values card, menu
     */
    variant: {
      type: String,
      default: 'card',
    },

    /**
     * Define a altura máxima do card ao usar a variante `menu`
     */
    maxMenuHeigth: {
      type: [String, Number],
      default: '350px',
    },
    /**
     * Repassa as props para o componente `v-menu`
     */
    menuProps: {
      type: Object,
      default: () => ({
        contentClass: 'mt-2',
        origin: 'top center',
        offsetY: true,
        offsetOverflow: true,
        closeOnContentClick: false,
      }),
    },
    /**
     * Aplica um seta no activator padrão do menu
     */
    showMenuArrow: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isMenuOpened: false,
      preventReaded: false,
    }
  },
  methods: {
    onClickClose() {
      /**
       * Emitido quando o botão de fechar é clicado
       */
      this.$emit('close')
    },
    onClickClear() {
      /**
       * Emitido quando o botão de limpar todas as notificações
       * for clicado
       */
      this.$emit('clear')
    },
    onClickRemove(notification) {
      this.preventReaded = true
      /**
       * Emitido quando o botão de limpar uma notificação é clicado
       *
       * @param {object} notification - Notificação a ser removida
       */
      this.$emit('remove', notification)
      setTimeout(() => {
        this.preventReaded = false
      }, 100)
    },
    onClickNotification(notification) {
      if (this.preventReaded) {
        return
      }
      /**
       * Emitido quando uma notificação é clicada
       *
       * @param {object} notification - Notificação clicada
       */
      this.$emit('click', notification)
    },
    onClickReaded() {
      /**
       * Emitido quando botão de todas lidas é clicado
       */
      this.$emit('readed')
    },
  },
}
</script>

<style lang="scss" scoped>
.zoox-notification-center-container {
  height: 100vh;
  position: fixed;
  z-index: 100;
}

.arrow-menu {
  transform: translateX(-50%);
  position: absolute;
  border-bottom: solid 8px var(--white) !important;
  border-right: solid 8px transparent;
  border-left: solid 8px transparent;
  top: 27px;
  left: 50%;
  height: 0px;
  width: 0px;
  z-index: 9;
}
</style>
